import { styled } from "@linaria/react"

import { Button } from "@/components/Button/Button.tsx"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { GithubFill, Icon16, Icon32, LockRegular } from "@/icons/Icons.ts"
import { formatDate } from "@/lib/date.ts"
import { ellipsis } from "@/styles/mixins.ts"

import type { GitHubRepo } from "@/lib/client/generated/TypeDefinitions.ts"

export const GitHubRepositoryList = ({ repositories, hasMore, onRepositorySelect, onShowMore }: {
  repositories: GitHubRepo[]
  hasMore: boolean
  onRepositorySelect: (repo: GitHubRepo) => void
  onShowMore: () => void
}) => {
  return (
    <RepositoryListContainer>
      {repositories.map((repo) => (
        <RepositoryItem
          key={repo.id}
          repo={repo}
          onSelect={onRepositorySelect}
        />
      ))}
      {hasMore && (
        <ShowMoreButton onClick={onShowMore}>
          Load more
        </ShowMoreButton>
      )}
    </RepositoryListContainer>
  )
}

const RepositoryItem = ({ repo, onSelect }: {
  repo: GitHubRepo
  onSelect: (repo: GitHubRepo) => void
}) => {
  return (
    <RepositoryItemContainer>
      <RepositoryItemContent>
        <RepositoryAvatar>
          <GithubFill className={Icon32} />
        </RepositoryAvatar>
        <RepositoryInfo>
          <RepositoryName>{repo.name}</RepositoryName>
          <RepositoryMeta>
            {repo.isPrivate && (
              <LockIcon>
                <LockRegular className={Icon16} />
              </LockIcon>
            )}
            <RepositoryDate>{formatDate(repo.updatedAt)}</RepositoryDate>
          </RepositoryMeta>
        </RepositoryInfo>
      </RepositoryItemContent>
      <ChooseButton
        kind="secondary"
        onClick={() => onSelect(repo)}
      >
        Choose
      </ChooseButton>
    </RepositoryItemContainer>
  )
}

const RepositoryListContainer = styled(Vertical)`
  gap: 8px;
  width: 100%;
`

const ShowMoreButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  color: var(--main-color);
  font-size: var(--small-font-size);
  cursor: pointer;
  text-align: center;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--main-color);
    outline-offset: 2px;
    border-radius: var(--border-radius-4);
  }
`

const RepositoryItemContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  height: 64px;
  width: 100%;
  padding: 14px 16px;

  border: 1px solid var(--separator-color);
  border-radius: var(--border-radius-20);

  background-color: var(--mask-color);
  box-shadow: 0 2px 20px 0 rgb(26 46 160 / 5%);
`

const RepositoryItemContent = styled(Horizontal)`
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`

const RepositoryAvatar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--background-color-10);
  flex-shrink: 0;
`

const RepositoryInfo = styled(Horizontal)`
  gap: 8px;
  min-width: 0;
  flex: 1;
`

const RepositoryName = styled.div`
  font-size: var(--small-font-size);
  font-weight: 500;

  color: var(--text-color);

  ${ellipsis};
`

const RepositoryMeta = styled(Horizontal)`
  align-items: center;
  gap: 6px;
`

const LockIcon = styled.div`
  display: flex;
  align-items: center;
`

const RepositoryDate = styled.div`
  font-size: var(--small-font-size);
  color: var(--secondary-text-color);
`

const ChooseButton = styled(Button)`
  flex-shrink: 0;
`
